import { useState, useRef, useEffect } from 'react';
import { List, Bell, MessageCircle, ChevronDown, Sun, Moon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/app/providers/ThemeProvider';
import styles from './Header.module.css';

/**
 * Props del componente Header
 * @param logoIcon - URL o import del ícono del logo
 * @param logoText - URL o import del texto del logo (opcional)
 * @param userAvatar - URL de la imagen del avatar del usuario
 * @param userName - Nombre del usuario a mostrar
 * @param userMenuItems - Array de items para el menú dropdown del usuario
 * @param onSidebarToggle - Callback cuando se hace clic en el toggle del sidebar móvil
 */
export interface HeaderProps {
  logoIcon: string;
  logoText?: string;
  userAvatar?: string;
  userName?: string;
  userMenuItems?: Array<{
    label: string;
    onClick: () => void;
    href?: string;
  }>;
  onSidebarToggle?: () => void;
}

export const Header = ({
  logoIcon,
  logoText,
  userAvatar,
  userName = 'Usuario',
  userMenuItems = [],
  onSidebarToggle,
}: HeaderProps) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [_isMobileMenuOpen, _setIsMobileMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLLIElement>(null);
  const { theme, toggleTheme } = useTheme();

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const handleSidebarToggle = () => {
    onSidebarToggle?.();
  };

  // Extraer solo el primer nombre del usuario
  const getFirstName = (fullName: string): string => {
    return fullName.split(' ')[0];
  };

  const displayName = getFirstName(userName);

  return (
    <div className={`${styles.navbar} ${styles.navbarStatic}`}>
      <div className={styles.containerFluid}>
        {/* Toggle sidebar móvil */}
        <div className={styles.mobileToggle}>
          <button
            type="button"
            className={`${styles.navbarToggler} ${styles.sidebarMobileToggle}`}
            onClick={handleSidebarToggle}
            aria-label="Toggle sidebar"
          >
            <List size={20} />
          </button>
        </div>

        {/* Logo */}
        <div className={styles.navbarBrand}>
          <Link to="/" className={styles.brandLink}>
            <img src={logoIcon} alt="Logo" className={styles.logoIcon} />
            {logoText && (
              <img
                src={logoText}
                alt="Logo Text"
                className={styles.logoText}
              />
            )}
          </Link>
        </div>

        {/* Acciones del header (notificaciones, mensajes, usuario) */}
        <ul className={styles.navActions}>
          {/* Notificaciones */}
          <li className={styles.navItem}>
            <button className={`${styles.navbarNavLink} ${styles.navbarNavLinkIcon}`}>
              <Bell size={20} />
              <span className={styles.badge}>2</span>
            </button>
          </li>

          {/* Mensajes */}
          <li className={styles.navItem}>
            <button className={`${styles.navbarNavLink} ${styles.navbarNavLinkIcon}`}>
              <MessageCircle size={20} />
            </button>
          </li>

          {/* Toggle de tema */}
          <li className={styles.navItem}>
            <button
              className={`${styles.navbarNavLink} ${styles.navbarNavLinkIcon}`}
              onClick={toggleTheme}
              title={theme === 'dark' || theme === 'sapphire' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
              aria-label="Toggle theme"
            >
              {theme === 'dark' || theme === 'sapphire' ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </li>

          {/* Menú usuario */}
          <li className={`${styles.navItem} ${styles.userMenuContainer}`} ref={userMenuRef}>
            <button
              className={`${styles.navbarNavLink} ${styles.userMenuButton}`}
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              aria-expanded={isUserMenuOpen}
            >
              <div className={styles.statusIndicatorContainer}>
                {userAvatar ? (
                  <img
                    src={userAvatar}
                    className={styles.userAvatar}
                    alt={displayName}
                  />
                ) : (
                  <div className={styles.userAvatarPlaceholder}>
                    {displayName.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className={styles.statusIndicator}></span>
              </div>
              <span className={styles.userName}>{displayName}</span>
              <ChevronDown
                size={16}
                className={`${styles.chevronIcon} ${isUserMenuOpen ? styles.rotated : ''}`}
              />
            </button>

            {/* Dropdown del usuario */}
            {isUserMenuOpen && userMenuItems.length > 0 && (
              <div className={`${styles.dropdownMenu} ${styles.dropdownMenuEnd}`}>
                {userMenuItems.map((item, index) => (
                  <button
                    key={index}
                    className={styles.dropdownItem}
                    onClick={() => {
                      item.onClick();
                      setIsUserMenuOpen(false);
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </li>
        </ul>
      </div>
    </div>
  );
};

